/**
 * AnimatedCounter
 * -------------------------------------------------------------
 * Parses a value like "10,000+" or "4.9" into its numeric part,
 * then smoothly eases it into view the first time it scrolls
 * into the viewport. Preserves any suffix ("+", "k", "%", etc.).
 */
import { useEffect, useRef, useState } from 'react';
import { motion, useInView, useMotionValue, useTransform, animate } from 'framer-motion';

function parseValue(raw) {
  if (raw == null) return { number: 0, prefix: '', suffix: '', decimals: 0 };
  const str = String(raw);
  const match = str.match(/^([^\d.-]*)([\d.,-]+)(.*)$/);
  if (!match) return { number: 0, prefix: '', suffix: str, decimals: 0 };

  const [, prefix = '', rawNum = '0', suffix = ''] = match;
  const cleaned = rawNum.replace(/,/g, '');
  const number = Number(cleaned);
  const decimals = cleaned.includes('.') ? (cleaned.split('.')[1]?.length || 0) : 0;
  return {
    number: Number.isFinite(number) ? number : 0,
    prefix,
    suffix,
    decimals,
  };
}

function formatNumber(n, decimals) {
  return n.toLocaleString('en-US', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
}

export default function AnimatedCounter({
  value,
  duration = 1.6,
  delay = 0,
  className = '',
}) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, amount: 0.4 });

  const { number, prefix, suffix, decimals } = parseValue(value);

  const mv = useMotionValue(0);
  const rounded = useTransform(mv, (latest) => formatNumber(latest, decimals));
  const [display, setDisplay] = useState(formatNumber(0, decimals));

  useEffect(() => {
    const unsub = rounded.on('change', (v) => setDisplay(v));
    return () => unsub();
  }, [rounded]);

  useEffect(() => {
    if (!inView) return;
    const controls = animate(mv, number, {
      duration,
      delay,
      ease: [0.25, 0.1, 0.25, 1],
    });
    return () => controls.stop();
  }, [inView, number, duration, delay, mv]);

  return (
    <motion.span ref={ref} className={className} aria-label={String(value)}>
      {prefix}
      {display}
      {suffix}
    </motion.span>
  );
}
